import type { FC } from '../../lib/teact/teact';
import { memo } from '../../lib/teact/teact';
import { getActions } from '../../global';

import { approveQrToken } from '../../util/qrApproveLogin';
import renderText from '../common/helpers/renderText';

import useCurrentOrPrev from '../../hooks/useCurrentOrPrev';
import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import ConfirmDialog from '../ui/ConfirmDialog';

export type OwnProps = {
  request?: {
    token: string;
    platform?: string;
    ip?: string;
  };
};

// Approval prompt shown on an already-authenticated device when it opens a scanned QR-login URL.
// Confirming approves the other device's pending login (see `runQrApproveFlow`).
const QrApproveModal: FC<OwnProps> = ({ request }) => {
  const { closeQrApproveModal, showNotification } = getActions();

  const lang = useLang();
  const rendering = useCurrentOrPrev(request);

  const handleClose = useLastCallback(() => {
    closeQrApproveModal();
  });

  const handleApprove = useLastCallback(() => {
    const token = rendering?.token;
    closeQrApproveModal();
    if (!token) return;

    void approveQrToken(token).then((isApproved) => {
      showNotification({ message: isApproved ? lang('QrApproveDone') : lang('QrApproveFailed') });
    });
  });

  const device = [rendering?.platform, rendering?.ip].filter(Boolean).join(' • ');

  return (
    <ConfirmDialog
      isOpen={Boolean(request)}
      onClose={handleClose}
      title={lang('QrApproveTitle')}
      text={device ? undefined : lang('QrApproveText')}
      textParts={device
        ? renderText(lang('QrApproveTextDevice', { device }, { withNodes: true, withMarkdown: true }))
        : undefined}
      confirmLabel={lang('QrApproveConfirm')}
      confirmHandler={handleApprove}
    />
  );
};

export default memo(QrApproveModal);
